import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {HOTB} from "../HOTB";
import {gsap} from "gsap";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {GameAssets} from "../GameAssets";
import {GameSounds} from "../sound/GameSounds";
import {GameUtils} from "../utils/GameUtils";
import {ExtendedGameMode} from "../server/ExtendedGameMode";
import {HOTBGameData} from "../server/HOTBData";
import {AnimUtils} from "../utils/AnimUtils";
import {GameConfig} from "../GameConfig";
import {BitmapText} from "pixi.js";

export class WinCapView extends PIXI.Container implements StageLayer {
    private _button: ContinueButton;
    private _countUp: BasicBitmapCountUp;
    private _multiplier: BitmapText;
    private _countUpDuration: number = 10; // 6.9;
    private _winCapSpine: PIXI.spine.Spine;
    private _overlay: PIXI.Graphics;
    private _clickCallback: () => void;

    constructor() {
        super();
        this.name = "WinCapView";
        this.init();
        this.onResize();
    }

    public showView(callback: () => void): void {
        const tl: gsap.core.Timeline = gsap.timeline(),
            gameData: HOTBGameData = HOTB.model.gameData;

        tl.add((): void => {
            this._overlay.alpha = 0;
            this._overlay.visible = true;

            this._countUp.alpha = 0;
            this._countUp.visible = true;

            this._winCapSpine.alpha = 0;
            this._winCapSpine.visible = true;

            this._button.alpha = 0;
            this._button.visible = true;

            this._multiplier.alpha = 0;
            this._multiplier.visible = true;

            this._clickCallback = callback;
            HOTB.soundPlayer.playSound(GameSounds.WIN_CAP_SATAN_START);
        }, 0);

        tl.to(this._overlay, {
            alpha: 1,
            duration: 2
        });

        tl.add([
            AnimUtils.playSpineAnimation({
                spineObj: this._winCapSpine,
                animation: "countUp",
            }),
            gsap.to(this._overlay, {
                alpha: 0,
                duration: 1
            }),
            (): void => {
                this._countUp.alpha = 1;
                this._winCapSpine.alpha = 1;
                HOTB.soundPlayer.playLoopEffect(GameSounds.WIN_CAP_SATAN_COUNT_UP);
                HOTB.reelArea.stopAllBobbling();
            },
            this._countUp.getCountAnimation(0, gameData.accumulatedRoundWin, this._countUpDuration, "power1.easeOut")
        ]);

        tl.add([
            AnimUtils.playSpineAnimation({
                spineObj: this._winCapSpine,
                animation: "explosion"
            }),
            (): void =>{
                HOTB.soundPlayer.playSound(GameSounds.WIN_CAP_SATAN_EXPLOSION);
                HOTB.soundPlayer.stopLoopEffect(GameSounds.WIN_CAP_SATAN_COUNT_UP);
                HOTB.soundPlayer.playLoopEffect(GameSounds.WIN_CAP_SATAN_LOOP);
            }
        ]);

        tl.add((): void => {
            HOTB.gameBackground.playIdle(ExtendedGameMode.NORMAL);

            AnimUtils.playSpineAnimation({
                spineObj: this._winCapSpine,
                animation: "loop",
                playLoop: true
            });
            HOTB.keypadPlugin.setWin(HOTB.model.gameData.accumulatedRoundWin, true, GameUtils.isWinBelowStake());
            this._multiplier.alpha = 1;
            this._button.alpha = 1;
            this._button.enable(true);
        });

        if (GameUtils.isCapMachineGame()) {
            tl.add((): void => {
                GameUtils.toggleSymbols(false);
            });
            tl.add(HOTB.pickAndClickView.resetCaps(true));
            tl.add(HOTB.pickAndClickView.showBBIntro(GameConfig.CAP_MACHINE_DEF_POS_Y));
        } else {
            if (gameData.mode === ExtendedGameMode.CAPWIN_GAME) {
                tl.add((): void => {
                    GameUtils.toggleSymbols(true);
                });
                tl.add(HOTB.pickAndClickView.resetCaps(false));
            }
        }
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;

        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
            this._countUp.position.set(360, 425);
            this._multiplier.position.set(360, 515);
        } else {
            const centerBottom: number = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
            this._countUp.position.set(360, 500);
            this._multiplier.position.set(360, 590);
        }
    }

    private init(): void {
        this.createWinCapAnimation();
        this.createCountUp();
        this.createMultiplier();
        this.createOverlay();
        this.createContinueButton();
    }

    private createWinCapAnimation(): void {
        this._winCapSpine = new PIXI.spine.Spine(HOTB.assetLoader.getSpineAsset("winCap"));
        this._winCapSpine.pivot.set(0.5, 0.5);
        this._winCapSpine.position.set(360, 360);
        this._winCapSpine.visible = false;
        this.addChild(this._winCapSpine);
    }

    private createCountUp(): void {
        this._countUp = new BasicBitmapCountUp({
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 24.5,
        }, 680, new PIXI.Point(0.5, 0.5));
        this._countUp.visible = false;
        this.addChild(this._countUp);
    }

    private createMultiplier(): void {
        this._multiplier = new PIXI.BitmapText("61446x", {
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 15,
            maxWidth: 680
        });
        this._multiplier.anchor = 0.5;
        this._multiplier.visible = false;
        this.addChild(this._multiplier);
    }

    private createOverlay(): void {
        this._overlay = new PIXI.Graphics();
        this._overlay.visible = false;
        this.setOverlayProps();
        this.addChild(this._overlay);
    }

    private setOverlayProps(): void {
        if (this._overlay) {
            const bounds: ScreenBounds = NolimitApplication.screenBounds;
            this._overlay.clear();
            this._overlay.beginFill(0x000000, 1);
            this._overlay.drawRect(-480, -480, 1680, 1680);
            this._overlay.endFill();
        }
    }

    private createContinueButton(): void {
        this._button = new ContinueButton();
        this._button.addClickCallback((): void => {
            this.onButtonClick();
        });
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false)
        this._button.visible = false;
        this.addChild(this._button);
    }

    private onButtonClick(): void {
        this._button.enable(false);

        HOTB.soundPlayer.stopLoopEffect(GameSounds.WIN_CAP_SATAN_LOOP);
        HOTB.soundPlayer.playSound(GameSounds.WIN_CAP_SATAN_END);
        HOTB.soundPlayer.playSound(GameSounds.CONTINUE_BUTTON);
        HOTB.soundPlayer.switchMusic(ExtendedGameMode.NORMAL);
        HOTB.soundPlayer.fadeMusic(1, 0.1);

        HOTB.model.isPlayingBonusSpins = false;
        const tl: gsap.core.Timeline = new gsap.core.Timeline(),
            duration: number = 0.5;

        tl.to([this._multiplier, this._winCapSpine, this._countUp, this._button], {
            duration: duration,
            alpha: 0
        }, 0);

        tl.add((): void => {
            this._winCapSpine.visible = false;
            this._multiplier.visible = false;
            this._overlay.visible = false;
            this._countUp.visible = false;
            this._button.visible = false;
            this._clickCallback();
        });
    }
}